import {CLStore} from 'common/src/redux/store';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import {DTConditionNode} from 'common/src/modules/decisionTree/decisionTree.types';
import {ConditionOperatorKey, ConditionType} from 'common/src/modules/conditions/conditions.types';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {MacroItemTypeValue} from 'common/src/modules/macros/macros.types';
import {PromptToCardAgentStatus} from '../../../promptToCardAgent/promptToCardAgent';
import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import getCardFromState from '../../utils/cardStateStorage/getCardFromState';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import tryPatchingCardAndReportResults from '../../utils/general/tryPatchingCardAndReportResults';
import setupCardProcessingData from '../../utils/general/setupCardProcessingData';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import addConditionOutputTool from './addConditionOutputTool';
import editConditionOutputTool from './editConditionOutputTool';
import getFilesTool from 'common/src/modules/shared/copilot/tools/getFilesTool';
import searchFeedValuesTool from '../../../../tools/conditions/tools/searchFeedValuesTool/searchFeedValuesTool';
import readValuesFromExcelTool from '../../../../tools/general/readValuesFromExcelTool/readValuesFromExcelTool';
import setSignalTool from './setSignalTool';
import searchSignalTool from './searchSignalTool';
import finalizeConditionTool from './finalizeConditionTool';
import deleteOutuputsTool from '../general/deleteOutuputTool';
import getAddOrUpdateCardPrompt from '../../../../tools/general/addOrUpdateCardTool/utils/getAddOrUpdateCardPrompt';
import {DateTime} from 'luxon';
import getCardDetailsTool from '../general/getCardDetailsTool';

const cardSpecificInstructions = `1. Use the ${V2CardAgentToolName.SearchSignal} tool to find the best matching signal for matching the user's request.
1.1. The signal has a property called *"shouldQueryOptionalValues"*. If this is true and you are adding/editing condition outputs values, you MUST use the ${V2CardAgentToolName.SearchFeedValues} tool to find the different values that are relevant for the signal.
1.2. Following the signal type returned by the ${V2CardAgentToolName.SearchSignal} tool, use the ${V2CardAgentToolName.AddConditionOutput} or ${V2CardAgentToolName.UpdateConditionOutput} tool to add or update the condition outputs.
2. When settings the outputs, DO NOT change the existing outputs, unless the condition has been changed or the user has requested to change the outputs values.
3. The user may ask to create outputs from an excel file. In such a case, use the ${V2CardAgentToolName.ReadValuesFromExcel} tool to read the outputs from the excel file.

### Condition card specific instructions
- A condition card always has a fallback output (type: ${OutputType.Fallback}), it cannot be removed or changed.

### IMPORTANT
- ALWAYS check the card's signal type and use the appropriate tool to add or update the condition outputs.
- DO NOT add fallback outputs of any kind, only set the outputs requested by the user.
- If the user did not ask to change the signal, skip the get and set signal steps.
- Once you have added or updated the condition outputs, use the ${V2CardAgentToolName.FinalizeCondition} tool to finalize the condition card.`;

export default function addOrUpdateConditionCardToolV2(store: CLStore) {
  async function execute(params: ToolExecuteParams<typeof addOrUpdateCardInputSchema>) {
    const {prompt, cardId: cardIdInput = 0} = params.input;
    const {toolUtils} = params;

    const cardId = cardIdInput ?? 0;

    const promptWithDateInstructions = prompt.concat(
      `\n When using dates, use the ISO format for the dates. 
        Today's date is ${DateTime.now().toISO()}.`,
    );

    //extend base prompt with existing card details
    const promptToUse = getAddOrUpdateCardPrompt({
      prompt: promptWithDateInstructions,
      cardId,
      store,
    });

    setupCardProcessingData<DTConditionNode>(cardId, () => ({
      id: NO_NODE_ID,
      type: NodeType.CONDITION,
      outputs: [
        {
          id: 1,
          name: 'Fallback',
          type: OutputType.Fallback,
          nodeId: NO_NODE_ID,
        },
      ],
      inputs: [],
      nextOutputId: 1,
      categories: [],
      condKey: '',
      condKeyType: MacroItemTypeValue.Text,
      condOperator: ConditionOperatorKey.Equal,
      signalDescription: '',
      conditionType: ConditionType.Text,
    }));

    //Invoke the standalone agent to generate the card schema
    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgent({
        name: V2CardAgentToolName.AddOrUpdateConditionCard,
        role: `Your purpose is to convert a natural language prompt to a ${
          cardTypeToName[NodeType.CONDITION]
        } card`,
        instructions: getPromptToCardAgentInstructions({
          cardType: NodeType.CONDITION,
          cardId,
          cardSpecificInstructions,
        }),
        tools: [
          setCardNameTool(),
          setCardDescriptionTool(),
          deleteOutuputsTool(),
          getFilesTool(),
          getCardDetailsTool(),

          //Condition tools
          searchSignalTool(store),
          setSignalTool(),
          addConditionOutputTool(),
          editConditionOutputTool(),
          searchFeedValuesTool(),
          readValuesFromExcelTool(),
          finalizeConditionTool(),
        ],
        responseSchema,
        model: getSetupAgentModel(),
      }),
      prompt: promptToUse,
    });

    const {status, chainOfThoughts} = result;

    // If the agent didn't generate a card schema, return no action result
    if (status === PromptToCardAgentStatus.NoAction) {
      return toolResult(
        JSON.stringify({status: PromptToCardAgentStatus.NoAction, chainOfThoughts}),
      );
    }

    //Get the card from the state, if it doesn't exist, use the existing card
    const cardFromState = getCardFromState();

    //Add or update the node state
    return tryPatchingCardAndReportResults(store, cardFromState, cardId, chainOfThoughts);
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateConditionCard,
    description: 'Add or update a condition card',
    displayName: 'Working on condition card',
    inputSchema: addOrUpdateCardInputSchema,
    execute,
  });
}
